import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import createFuzzySearch from '@nozbe/microfuzz';
import { CompanyIcon } from 'app/components/DetailsPages/shared/CommonGround/Icons';
import { CloseIcon, SearchIconSmall, SortingIcon } from 'app/components/shared/icons';
import { useFormikContext } from 'formik';
import useOnClickOutside from 'hooks/click-outside';
import useDebounce from 'hooks/useDebounce';
import { ThemeProvider } from 'styled-components';

import useHomeDrawerData from '../HomeDrawers/HomeBaseDrawer/useHomeDrawerData';
import {
  CompanySelectInput,
  CompanySelectOptionAlreadyAdded,
  CompanySelectOptionContainer,
  CompanySelectOptionLabel,
  CompanySelectOptionsContainer,
  CompanySelectPlaceholder,
  CompanySelectSelectedCompaniesContainer,
  CompanySelectSelectedCompanyContainer,
  CompanySelectSelectedCompanyLabel,
  CompanySelectSelectedCompanyRemoveContainer,
  CompanySelectWrapper,
} from './styled';

interface CompanySelectProps {
  name?: string;

  placeholder?: string;

  onChange?: (values: any[]) => void;

  allowMultiple?: boolean;

  preselectedValues?: any[];
  companyOptions?: any[];
  disabled?: boolean;
  optionsMessage?: string;
}

const companyIdentity = (company: any) =>
  company?.uuid ?? company?.id ?? company?.slug ?? company?.name;

const companyUuid = (company: any) => company?.uuid ?? company?.id;

export default function CompanySelect({
  placeholder = 'No company selected',
  name,
  onChange,
  allowMultiple = false,
  preselectedValues = [],
  companyOptions,
  disabled = false,
  optionsMessage,
}: CompanySelectProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const formikContext = useFormikContext();
  const { data: companies } = useHomeDrawerData('kyu_companies', {
    enabled: companyOptions === undefined,
  });
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const selectedCompanies = preselectedValues;
  const [currentInputValue, setCurrentInputValue] = useState<string>('');
  const debouncedValue = useDebounce(currentInputValue);
  const [list, setList] = useState<any[]>([]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && currentInputValue.trim() === '' && selectedCompanies.length > 0) {
      const lastCompany = selectedCompanies[selectedCompanies.length - 1];
      handleCompanyClick(lastCompany);
    }
  };

  const options = useMemo(() => {
    if (companyOptions !== undefined)
      return [...companyOptions].sort((a, b) => a.name.localeCompare(b.name));
    if (!companies) return [];

    let finalCompanies = [];
    companies.forEach((company) => {
      finalCompanies = [...finalCompanies, company];

      if (company.partner_companies && company.partner_companies.length > 0) {
        finalCompanies = [...finalCompanies, ...company.partner_companies];
      }
    });

    return finalCompanies.sort((a, b) => a.name.localeCompare(b.name));
  }, [companies, companyOptions]);

  const handleCompanyClick = (company) => {
    if (disabled) return;
    let finalCompanies = [];

    const identity = companyIdentity(company);

    const companyIndex = selectedCompanies.findIndex(
      (selectedCompany) => companyIdentity(selectedCompany) === identity
    );

    finalCompanies =
      companyIndex === -1
        ? allowMultiple
          ? [...selectedCompanies, company]
          : [company]
        : selectedCompanies.filter(
            (selectedCompany) => companyIdentity(selectedCompany) !== identity
          );

    if (name) {
      formikContext.setFieldValue(name, finalCompanies.map(companyUuid).filter(Boolean));
    }

    setCurrentInputValue('');
    onChange?.(finalCompanies);
    setIsOpen(false);
  };

  const fuzzySearch = useMemo(() => {
    if (options) {
      return createFuzzySearch(options, { key: 'name' });
    }
  }, [options]);

  const fuzzySearchCallback = useCallback(
    (value) => {
      if (fuzzySearch) {
        return fuzzySearch(value).map((result) => result.item);
      }
    },
    [debouncedValue, fuzzySearch]
  );

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentInputValue(e.target.value);
  };

  useEffect(() => {
    if (fuzzySearch && debouncedValue.trim() !== '') {
      setList(fuzzySearchCallback(debouncedValue));
    } else {
      setList(options);
    }
  }, [debouncedValue, fuzzySearchCallback, options]);

  useOnClickOutside(wrapperRef, () => {
    if (isOpen) {
      formikContext.setFieldTouched(name, true, false);
    }
    setIsOpen(false);
  });

  const handleWrapperClick = () => {
    if (isOpen || disabled) return;

    setIsOpen(true);
  };

  const handleCompanyRemoveClick = (
    company: any,
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    e.stopPropagation();
    if (disabled) return;
    const identity = companyIdentity(company);
    const finalCompanies = selectedCompanies.filter(
      (selectedCompany) => companyIdentity(selectedCompany) !== identity
    );

    if (name) {
      formikContext.setFieldValue(name, finalCompanies.map(companyUuid).filter(Boolean));
    }

    if (isOpen) {
      setIsOpen(false);
    }
    onChange?.(finalCompanies);
  };

  const isCompanySelected = useCallback(
    (company) => {
      const identity = companyIdentity(company);
      return selectedCompanies.some(
        (selectedCompany) => companyIdentity(selectedCompany) === identity
      );
    },
    [selectedCompanies]
  );

  const isError = useMemo<boolean>(() => {
    return formikContext.errors[name] && formikContext.touched[name];
  }, [formikContext.errors[name], formikContext.touched[name], name]);

  return (
    <ThemeProvider theme={{ isError }}>
      <CompanySelectWrapper ref={wrapperRef} onClick={handleWrapperClick}>
        <input type="hidden" name={name} value={formikContext.values[name]} />
        {selectedCompanies.length === 0 ? (
          <CompanySelectPlaceholder>{placeholder}</CompanySelectPlaceholder>
        ) : (
          <CompanySelectSelectedCompaniesContainer>
            {selectedCompanies.map((company) => (
              <CompanySelectSelectedCompanyContainer key={companyIdentity(company)}>
                <CompanyIcon width={16} height={16} />
                <CompanySelectSelectedCompanyLabel>
                  {company.name}
                </CompanySelectSelectedCompanyLabel>
                <CompanySelectSelectedCompanyRemoveContainer
                  data-action="remove"
                  onClick={(e) => handleCompanyRemoveClick(company, e)}
                >
                  <CloseIcon width={9} height={9} fill="var(--color-text)" fillOpacity="1" />
                </CompanySelectSelectedCompanyRemoveContainer>
              </CompanySelectSelectedCompanyContainer>
            ))}
            <CompanySelectInput
              disabled={disabled}
              ref={inputRef}
              placeholder={selectedCompanies.length > 0 ? null : placeholder}
              value={currentInputValue}
              onChange={handleOnChange}
              onKeyDown={handleKeyDown}
            />
          </CompanySelectSelectedCompaniesContainer>
        )}
        <SearchIconSmall width={24} height={24} fill="var(--color-primary)" />
        {isOpen ? (
          <CompanySelectOptionsContainer>
            {list.length === 0 ? (
              <p role="status">{optionsMessage || 'No companies found.'}</p>
            ) : null}
            {list?.map((company) => (
              <CompanySelectOptionContainer
                key={companyIdentity(company)}
                onClick={() => handleCompanyClick(company)}
              >
                <CompanySelectOptionLabel alreadyAdded={isCompanySelected(company)}>
                  {company.name}
                </CompanySelectOptionLabel>
                {isCompanySelected(company) ? (
                  <CompanySelectOptionAlreadyAdded>ALREADY ADDED</CompanySelectOptionAlreadyAdded>
                ) : null}
              </CompanySelectOptionContainer>
            ))}
          </CompanySelectOptionsContainer>
        ) : null}
      </CompanySelectWrapper>
    </ThemeProvider>
  );
}
